/*
 * @stayai — Recharge "Storefront Experiences" cart-drawer subscription-plan filter.
 *
 * Problem: the Recharge SFE cart drawer renders <select name="selling_plan"> options
 * from a variant's Shopify selling_plan_allocations across ALL subscription apps
 * (Recharge 294517 + Stay 5859381), with no app_id filter. Under coexistence a shopper
 * can pick the wrong app's plan from Recharge's cart (§5c cart-payload spill). The drawer
 * is React inside an OPEN shadow root (#recharge-storefront-experiences__cart-drawer),
 * unreachable from Liquid — so this is the client-side lever (playbook Appendix D shape a).
 *
 * What it does: whenever a selling_plan <select> appears in the drawer shadow root, remove
 * every option whose plan's app_id != ACTIVE_APP_ID. Keeps the active app's plans, the
 * "One-time" option, and any unknown/uncached plan (never blanks the UI). If the selected
 * option gets removed, resets to a safe active-app default and dispatches change via the
 * React value setter so the cart line re-syncs.
 *
 * Active app id: single source snippets/active-subscription-app-id.liquid, passed in as
 * window.STAYAI_ACTIVE_APP_ID from theme.liquid. Flip the app there, not here.
 */
(function () {
  "use strict";

  var ACTIVE_APP_ID = String(window.STAYAI_ACTIVE_APP_ID || "").trim();
  if (!ACTIVE_APP_ID) return; // no toggle value -> do nothing

  var stay_debug = true; // @stayai strip before prod (search 'stay_debug')
  function log() { if (stay_debug) console.log.apply(console, [">>> [stayai] cart-fix:"].concat([].slice.call(arguments))); }

  var DRAWER_HOST_ID = "recharge-storefront-experiences__cart-drawer";
  var SELECT_SEL = 'select[name="selling_plan"]';
  var KEEP_VALUES = { ONETIME: true }; // non-plan options we never strip

  // ---- plan_id -> app_id map, built from /cart.js handles -> /products/<handle>.js ----
  var planAppId = Object.create(null);      // { "<selling_plan_id>": "<app_id>" }
  var productCache = Object.create(null);   // handle -> Promise (fetch once)
  var lastCartFetch = 0;

  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch("/products/" + handle + ".js", { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (p && p.selling_plan_groups) {
          p.selling_plan_groups.forEach(function (g) {
            (g.selling_plans || []).forEach(function (sp) { planAppId[String(sp.id)] = String(g.app_id); });
          });
        }
      })
      .catch(function () {}); // degrade to show-all on failure
    return productCache[handle];
  }

  function refreshMap() {
    lastCartFetch = Date.now();
    return fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items) return;
        var handles = {};
        cart.items.forEach(function (it) { if (it.handle) handles[it.handle] = true; });
        return Promise.all(Object.keys(handles).map(fetchProduct));
      })
      .catch(function () {});
  }

  // React-safe value setter (a plain el.value / native change won't update React state)
  function setNativeValue(el, value) {
    var desc = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el), "value");
    if (desc && desc.set) desc.set.call(el, value); else el.value = value;
  }

  // Strip foreign options from a single <select>; repair selection if needed.
  function filterSelect(select) {
    var before = select.options.length;
    var removedSelected = false;
    [].slice.call(select.options).forEach(function (opt) {
      if (KEEP_VALUES[opt.value]) return;         // keep One-time
      var appId = planAppId[opt.value];
      if (!appId) return;                         // unknown -> keep (never blank the UI)
      if (appId !== ACTIVE_APP_ID) {
        if (opt.selected) removedSelected = true;
        opt.remove();
      }
    });

    if (removedSelected) {
      var fallback = null;                        // prefer first active-app plan, else One-time
      for (var i = 0; i < select.options.length; i++) {
        if (select.options[i].value !== "ONETIME") { fallback = select.options[i].value; break; }
      }
      if (fallback === null && select.options.length) fallback = select.options[select.options.length - 1].value;
      if (fallback !== null) {
        setNativeValue(select, fallback);
        select.dispatchEvent(new Event("change", { bubbles: true }));
        log("removed foreign selected plan; reset to", fallback);
      }
    }
    if (before !== select.options.length) log("stripped", before - select.options.length, "foreign option(s)");
  }

  // Find selling_plan selects, descending into any (open) nested shadow roots.
  function deepSelects(root, acc) {
    acc = acc || [];
    acc.push.apply(acc, root.querySelectorAll(SELECT_SEL));
    root.querySelectorAll("*").forEach(function (el) { if (el.shadowRoot) deepSelects(el.shadowRoot, acc); });
    return acc;
  }

  function runFilter(shadow) {
    var selects = deepSelects(shadow);
    if (!selects.length) return;
    var hasUnknown = selects.some(function (s) {
      return [].some.call(s.options, function (o) { return !KEEP_VALUES[o.value] && !(o.value in planAppId); });
    });
    if (hasUnknown && Date.now() - lastCartFetch > 1500) {
      refreshMap().then(function () { selects.forEach(filterSelect); });
    } else {
      selects.forEach(filterSelect);
    }
  }

  var observer = null; // set once the drawer exists; null means "not observing"

  function attach(host) {
    var shadow = host && host.shadowRoot;
    if (!shadow || observer) return false; // need the shadow root; attach exactly once
    var debounce;
    observer = new MutationObserver(function () {
      if (!host.isConnected) { // drawer host removed from DOM -> stop observing, allow re-attach
        observer.disconnect();
        observer = null;
        log("drawer host removed; observer disconnected");
        return;
      }
      clearTimeout(debounce);
      debounce = setTimeout(function () { runFilter(shadow); }, 30);
    });
    observer.observe(shadow, { childList: true, subtree: true });
    runFilter(shadow); // initial pass
    log("attached to drawer shadow root (ACTIVE_APP_ID=" + ACTIVE_APP_ID + ")");
    return true;
  }

  function tryAttach() {
    return attach(document.getElementById(DRAWER_HOST_ID));
  }

  // The SFE drawer mounts async. Poll for its host + shadow root, attach once, then stop.
  // If no Recharge cart drawer ever appears (app not on this page), give up cleanly and
  // never start a MutationObserver.
  function init() {
    if (tryAttach()) return;
    var tries = 0;
    var poll = setInterval(function () {
      if (tryAttach()) {
        clearInterval(poll);
      } else if (++tries > 40) { // ~20s self-terminate
        clearInterval(poll);
        log("no Recharge cart drawer found (~20s); not observing");
      }
    }, 500);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
